// QR codes, folders, and templates provider
// QR code and folder data supports per-piid access (home + partner sites)
//
import PropTypes from 'prop-types';
import {
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';

import {
  answerRefusesPrivilege,
  config,
  displayJson,
  logError,
} from '../../utils';
import useAuth from '../auth/useAuth';
import usePushEvent from '../push/usePushEvent';
import useTownConfig from '../townConfig/useTownConfig';

import QrsContext from './QrsContext';

const initialState = {
  qrCodesByPiid: { home: [] },
  qrCodesStatusByPiid: { home: 'init' },
  qrFoldersByPiid: { home: undefined },
  qrFoldersStatusByPiid: { home: 'init' },
  qrFoldersDepth: 10,
  // The plans the caller may see (/plans/list), one per folder carrying one
  plans: undefined,
  qrTemplatesByPiid: { home: [] },
  qrTemplatesStatusByPiid: { home: 'init' },
};

const folderKey = (piid) => ((!piid || (piid === 'home')) ? 'home' : piid);

// Same list as pagePrivilegesNeeded.qrs: any of them opens the QR routes
const qrPrivileges = ['qrView', 'qrManage', 'qrEdit'];

const stateReducer = (state, action) => {
  switch (action.type) {
    case 'reset':
      return initialState;

    case 'setQrCodesStatus': {
      const key = folderKey(action.piid);
      return ({
        ...state,
        qrCodesStatusByPiid: {
          ...state.qrCodesStatusByPiid,
          [key]: action.value,
        },
      });
    }

    case 'setQrCodes': {
      const key = folderKey(action.piid);
      return ({
        ...state,
        qrCodesByPiid: {
          ...state.qrCodesByPiid,
          [key]: action.qrCodes,
        },
        qrCodesStatusByPiid: {
          ...state.qrCodesStatusByPiid,
          [key]: 'ready',
        },
      });
    }

    case 'setFoldersStatus': {
      const key = folderKey(action.piid);
      return ({
        ...state,
        qrFoldersStatusByPiid: {
          ...state.qrFoldersStatusByPiid,
          [key]: action.value,
        },
      });
    }

    case 'setQrFolders': {
      const key = folderKey(action.piid);
      return ({
        ...state,
        qrFoldersByPiid: {
          ...state.qrFoldersByPiid,
          [key]: action.folders,
        },
        qrFoldersStatusByPiid: {
          ...state.qrFoldersStatusByPiid,
          [key]: 'ready',
        },
      });
    }

    case 'setFoldersDepth':
      return ({
        ...state,
        qrFoldersDepth: action.value,
      });

    case 'setPlans':
      return ({
        ...state,
        plans: action.plans,
      });

    case 'setQrTemplatesStatusByPiid': {
      const key = folderKey(action.piid);
      return ({
        ...state,
        qrTemplatesStatusByPiid: {
          ...state.qrTemplatesStatusByPiid,
          [key]: action.value,
        },
      });
    }

    case 'setQrTemplatesByPiid': {
      const key = folderKey(action.piid);
      return ({
        ...state,
        qrTemplatesByPiid: {
          ...state.qrTemplatesByPiid,
          [key]: action.qrTemplates,
        },
        qrTemplatesStatusByPiid: {
          ...state.qrTemplatesStatusByPiid,
          [key]: 'ready',
        },
      });
    }

    default:
      logError(
        `QrsProvider unknown action=${displayJson(action)}`,
        'QrsProvider.reducer.unknownAction',
        { actionType: action?.type },
      );
      throw new Error();
  }
};

function QrsProvider(props) {
  const {
    children,
  } = props;
  const { auth } = useAuth();
  const {
    cstmrPost,
    info,
    authStatus,
  } = auth;
  const {
    cuid,
    rlid,
  } = info || {};
  const { townConfig } = useTownConfig();
  const { getPrivileges } = townConfig;
  const [state, dispatch] = useReducer(stateReducer, initialState);

  // /qr/* is privilege-gated server-side, per context. undefined = the context's
  // privileges are not loaded yet, so the caller retries when they arrive.
  const qrAllowedInContext = useCallback(
    (key) => {
      const privs = getPrivileges((key === 'home') ? undefined : key)?.privileges;
      if (!privs) return undefined;
      return privs.some((priv) => qrPrivileges.includes(priv));
    },
    [getPrivileges],
  );

  const refreshQrCodes = useCallback(
    async (piid) => {
      if (authStatus !== 'loggedIn') return;
      const key = folderKey(piid);

      const allowed = qrAllowedInContext(key);
      if (allowed === undefined) return;
      if (!allowed) {
        dispatch({
          type: 'setQrCodes',
          piid: key,
          qrCodes: [],
        });
        return;
      }

      let error;
      let res;

      dispatch({
        type: 'setQrCodesStatus',
        piid: key,
        value: 'fetch',
      });
      try {
        const postArgs = {
          cuid,
          rlid,
          urlPath: config.town.back.qr.list,
          payload: {},
        };
        if (key !== 'home') postArgs.piid = piid;

        res = await cstmrPost(postArgs);
        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (!error) {
        // A privilege refusal answers 200 with a status flag instead of a list
        const rows = (answerRefusesPrivilege(res)) ? [] : res.body.rslt.data;
        const qrCodesRslt = rows.map(
          (thisQr) => {
            const { qrAttr, ...otherQrProps } = thisQr;
            return ({
              ...otherQrProps,
              ...qrAttr,
            });
          },
        );

        dispatch({
          type: 'setQrCodes',
          piid: key,
          qrCodes: qrCodesRslt,
        });
        return;
      }
      logError(
        `refreshQrCodes(${key}) Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'QrsProvider.refreshQrCodes.error',
        {
          piid: key,
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
          ...(res?.status && { resStatus: res.status }),
        },
      );
      dispatch({
        type: 'setQrCodesStatus',
        piid: key,
        value: 'error',
      });
    },
    [authStatus, cstmrPost, cuid, rlid, qrAllowedInContext],
  );

  const refreshQrTemplates = useCallback(
    async (piid) => {
      if (authStatus !== 'loggedIn') return;
      const key = folderKey(piid);
      let error;
      let res;

      dispatch({
        type: 'setQrTemplatesStatusByPiid',
        piid: key,
        value: 'fetch',
      });
      try {
        const postArgs = {
          cuid,
          rlid,
          urlPath: config.town.back.qr.template.list,
          payload: {},
        };
        if (key !== 'home') postArgs.piid = piid;

        res = await cstmrPost(postArgs);
        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (!error) {
        dispatch({
          type: 'setQrTemplatesByPiid',
          piid: key,
          qrTemplates: res.body.rslt.data,
        });
        return;
      }
      logError(
        `refreshQrTemplates(${key}) Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'QrsProvider.refreshQrTemplates.error',
        {
          piid: key,
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
          ...(res?.status && { resStatus: res.status }),
        },
      );
      dispatch({
        type: 'setQrTemplatesStatusByPiid',
        piid: key,
        value: 'error',
      });
    },
    [authStatus, cstmrPost, cuid, rlid],
  );

  const editQrCode = useCallback(
    async (qr, piid) => {
      if (authStatus !== 'loggedIn') return false;
      let error;
      let res;

      try {
        const {
          qrid, qrLbl, qrRef, qrFldrId, qrType, qtid,
        } = qr;

        const payload = {
          qrid,
          // This field is displayed with a prefix; it is expected to often have a leading space character
          qrLbl: qrLbl.replace(/\s+/g, ' ').trimEnd(),
          qrRef,
          qrFldrId,
          qrType,
          qtid,
        };

        // console.log(`Edit QR code at ${url}, payload=${displayJson(payload)}`);
        // console.log(` newStaffArray=${displayJson(newStaffArray)}`);
        res = await cstmrPost({
          cuid,
          rlid,
          ...((folderKey(piid) !== 'home') && { piid }),
          urlPath: config.town.back.qr.edit,
          payload,
        });

        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        } else if (answerRefusesPrivilege(res)) {
          error = new Error('notAllowed');
        }
      } catch (err) {
        error = err;
      }

      if (!error) {
        return true;
      }

      logError(
        `editQrCode Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'QrsProvider.editQrCode.error',
        {
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
        },
      );
      return false;
    },
    [authStatus, cstmrPost, cuid, rlid],
  );

  const deleteQrCode = useCallback(
    async (qrid, piid) => {
      if (authStatus !== 'loggedIn') return false;
      let error;
      let res;

      try {
        const payload = { qrid };

        // console.log(`Delete QR code at ${url}, payload=${payload}`);
        // console.log(` newStaffArray=${displayJson(newStaffArray)}`);
        res = await cstmrPost({
          cuid,
          rlid,
          ...((folderKey(piid) !== 'home') && { piid }),
          urlPath: config.town.back.qr.delete,
          payload,
        });

        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        } else if (answerRefusesPrivilege(res)) {
          error = new Error('notAllowed');
        }
      } catch (err) {
        error = err;
      }

      if (!error) {
        return true;
      }
      logError(
        `deleteQrCode Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'QrsProvider.deleteQrCode.error',
        {
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
        },
      );
      return false;
    },
    [authStatus, cstmrPost, cuid, rlid],
  );

  const disableQrCode = useCallback(
    async (qrid, message, piid) => {
      if (authStatus !== 'loggedIn') return false;
      let error;
      let res;

      try {
        const status = {
          disabled: {
            always: 1,
          },
        };
        if (message) status.disabled.message = message.trim();
        const payload = {
          qrid,
          status,
        };

        // console.log(`Disable QR code at ${url}, payload=${displayJson(payload)}`);
        // console.log(` newStaffArray=${displayJson(newStaffArray)}`);
        res = await cstmrPost({
          cuid,
          rlid,
          ...((folderKey(piid) !== 'home') && { piid }),
          urlPath: config.town.back.qr.setStatus,
          payload,
        });

        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        } else if (answerRefusesPrivilege(res)) {
          error = new Error('notAllowed');
        }
      } catch (err) {
        error = err;
      }

      if (!error) {
        return true;
      }
      logError(
        `disableQrCode Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'QrsProvider.disableQrCode.error',
        {
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
        },
      );
      return false;
    },
    [authStatus, cstmrPost, cuid, rlid],
  );

  const enableQrCode = useCallback(
    async (qrid, piid) => {
      if (authStatus !== 'loggedIn') return false;
      let error;
      let res;

      try {
        // Remove disabled entry from status
        const status = {};
        const payload = {
          qrid,
          status,
        };

        // console.log(`Enable QR code at ${url}, payload=${displayJson(payload)}`);
        // console.log(` newStaffArray=${displayJson(newStaffArray)}`);
        res = await cstmrPost({
          cuid,
          rlid,
          ...((folderKey(piid) !== 'home') && { piid }),
          urlPath: config.town.back.qr.setStatus,
          payload,
        });

        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        } else if (answerRefusesPrivilege(res)) {
          error = new Error('notAllowed');
        }
      } catch (err) {
        error = err;
      }

      if (!error) {
        return true;
      }
      logError(
        `enableQrCode Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'QrsProvider.enableQrCode.error',
        {
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
        },
      );
      return false;
    },
    [authStatus, cstmrPost, cuid, rlid],
  );

  const folderDepthRef = useRef(state.qrFoldersDepth);
  folderDepthRef.current = state.qrFoldersDepth;

  // Plans belong to the customer's own folders, so they come along with the home folder list
  const refreshPlans = useCallback(
    async () => {
      try {
        const res = await cstmrPost({
          cuid,
          rlid,
          urlPath: config.town.back.plans.list,
          payload: {},
        });
        const data = res.body?.rslt?.data;
        dispatch({
          type: 'setPlans',
          plans: (Array.isArray(data) && (!data[0]?.notAllowed)) ? data : [],
        });
      } catch (err) {
        logError(
          `refreshPlans ${err?.message ?? err}`, // NOSONAR error stringified for logging
          'QrsProvider.refreshPlans.error',
          {
            err: err?.message,
            status: err?.status,
          },
        );
      }
    },
    [cstmrPost, cuid, rlid],
  );

  const refreshQrFolders = useCallback(
    async (piid) => {
      if (authStatus !== 'loggedIn') return;
      const key = folderKey(piid);

      const allowed = qrAllowedInContext(key);
      if (allowed === undefined) return;
      if (!allowed) {
        dispatch({
          type: 'setQrFolders',
          piid: key,
          folders: [],
        });
        return;
      }

      let error;
      let res;

      dispatch({
        type: 'setFoldersStatus',
        piid: key,
        value: 'fetch',
      });
      try {
        const postArgs = {
          cuid,
          rlid,
          urlPath: config.town.back.qr.folder.list,
          payload: { depth: folderDepthRef.current },
        };
        if (key !== 'home') postArgs.piid = piid;

        res = await cstmrPost(postArgs);
        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (!error) {
        const data = res.body.rslt.data;

        // A privilege refusal answers 200 with a status flag instead of a list
        dispatch({
          type: 'setQrFolders',
          piid: key,
          folders: (data?.[0]?.notAllowed) ? [] : data,
        });
        // Use void to make it clear we do not want to wait this async function:
        if (key === 'home') void refreshPlans();
        return;
      }
      logError(
        `refreshQrFolders(${key}) Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'QrsProvider.refreshQrFolders.error',
        {
          piid: key,
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
          ...(res?.status && { resStatus: res.status }),
        },
      );
      dispatch({
        type: 'setFoldersStatus',
        piid: key,
        value: 'error',
      });
    },
    [authStatus, cstmrPost, cuid, rlid, qrAllowedInContext, refreshPlans],
  );

  const createQrFolder = useCallback(
    async (qrFldrParentId, qrFldrLbl, qrFldrRef, qrFldrStyle, qrFldrType, piid) => {
      if (authStatus !== 'loggedIn') return ({ error: 'notLoggedIn' });
      let error;
      let res;
      const key = folderKey(piid);

      if (state.qrFoldersStatusByPiid[key] !== 'ready') {
        logError(
          'createQrFolder, not ready',
          'QrsProvider.createQrFolder.notReady',
          {
            key,
            status: state.qrFoldersStatusByPiid[key],
          },
        );
        return ({ error: 'notReady' });
      }

      const effectiveType = qrFldrType ?? 'qr';
      if (!(qrFldrLbl && qrFldrLbl.length)) {
        logError(
          'createQrFolder bad parameters: missing label',
          'QrsProvider.createQrFolder.missingLabel',
          {
            qrFldrParentId,
            qrFldrType: effectiveType,
            hasLbl: !!qrFldrLbl,
          },
        );
        return ({ error: 'badParameters' });
      }
      if ((effectiveType === 'qr') && (!qrFldrParentId)) {
        logError(
          'createQrFolder bad parameters: qr folder requires parent',
          'QrsProvider.createQrFolder.missingParent',
          {
            qrFldrType: effectiveType,
            qrFldrParentId,
          },
        );
        return ({ error: 'badParameters' });
      }

      dispatch({
        type: 'setFoldersStatus',
        piid: key,
        value: 'busy',
      });
      try {
        // console.log(`Get all QR codes at ${url}`);

        const folderData = {
          qrFldrLbl: qrFldrLbl.replace(/\s+/g, ' ').trim(),
          // fake,
        };
        if (qrFldrParentId) folderData.qrFldrParentId = qrFldrParentId;
        if (qrFldrType) folderData.qrFldrType = qrFldrType;

        // Handle style object with color, imgId, bannerId
        if (qrFldrStyle) {
          if (qrFldrStyle.color) folderData.qrFldrColr = qrFldrStyle.color;
          if (qrFldrStyle.imgId) folderData.qrFldrImgId = qrFldrStyle.imgId;
          if (qrFldrStyle.bannerId) folderData.qrFldrBannerId = qrFldrStyle.bannerId;
          if (qrFldrStyle.bannerId) folderData.qrFldrPublicLbl = qrFldrStyle.publicLbl;
        }

        const payload = {
          folders: [folderData],
        };
        if (qrFldrRef?.length) payload.qrFldrRef = qrFldrRef.replace(/\s+/g, ' ').trim();

        res = await cstmrPost({
          cuid,
          rlid,
          ...((key !== 'home') && { piid }),
          urlPath: config.town.back.qr.folder.create,
          payload,
        });
        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        } else if (answerRefusesPrivilege(res)) {
          error = new Error('notAllowed');
        }
      } catch (err) {
        error = err;
      }
      dispatch({
        type: 'setFoldersStatus',
        piid: key,
        value: 'ready',
      });

      const rslt = {};
      if (!error) {
        // console.log(`createQrFolder res=${displayJson(res.body.rslt.data)}`);

        if (res.body.rslt.data?.length
            && res.body.rslt.data[0].qrFldrId) {
          rslt.qrFldrId = res.body.rslt.data[0].qrFldrId;
        }
      } else {
        logError(
          `createQrFolder Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
          'QrsProvider.createQrFolder.error',
          {
            err: error?.message ?? String(error), // NOSONAR error stringified for logging
            ...(error?.status && { status: error.status }),
            ...(res?.status && { resStatus: res.status }),
            qrFldrParentId,
            qrFldrType,
          },
        );
        rslt.error = error;
      }

      await refreshQrFolders(piid);
      return rslt;
    },
    [authStatus, state.qrFoldersStatusByPiid, cstmrPost, cuid, rlid, refreshQrFolders],
  );

  const editQrFolder = useCallback(
    async (qrFldrId, qrFldrParentId, qrFldrLbl, qrFldrRef, qrFldrStyle, piid) => {
      if (authStatus !== 'loggedIn') return ({ error: 'notLoggedIn' });
      let error;
      let res;
      const key = folderKey(piid);

      if (state.qrFoldersStatusByPiid[key] !== 'ready') {
        logError(
          'editQrFolder, not ready',
          'QrsProvider.editQrFolder.notReady',
          {
            key,
            status: state.qrFoldersStatusByPiid[key],
            qrFldrId,
          },
        );
        return ({ error: 'notReady' });
      }

      if (!(qrFldrId && qrFldrLbl?.length)) {
        logError(
          'editQrFolder bad parameters',
          'QrsProvider.editQrFolder.badParameters',
          {
            qrFldrId,
            hasLbl: !!qrFldrLbl,
            lblLength: qrFldrLbl?.length,
          },
        );
        return ({ error: 'badParameters' });
      }

      dispatch({
        type: 'setFoldersStatus',
        piid: key,
        value: 'busy',
      });
      try {
        const trimmedLbl = qrFldrLbl.replace(/\s+/g, ' ').trim();

        // Update the folder (label and style)
        const payload = { qrFldrId };

        payload.qrFldrParentId = qrFldrParentId;
        payload.qrFldrLbl = trimmedLbl;
        if (qrFldrRef?.length) {
          payload.qrFldrRef = qrFldrRef.replace(/\s+/g, ' ').trim();
        }

        // Handle style object with color, imgId, bannerId, publicLbl
        // undefined = don't touch, null = clear, value = update
        if (qrFldrStyle) {
          if (qrFldrStyle.color !== undefined) payload.qrFldrColr = qrFldrStyle.color;
          if (qrFldrStyle.imgId !== undefined) payload.qrFldrImgId = qrFldrStyle.imgId;
          if (qrFldrStyle.bannerId !== undefined) payload.qrFldrBannerId = qrFldrStyle.bannerId;
          if (qrFldrStyle.publicLbl !== undefined) {
            payload.qrFldrPublicLbl = qrFldrStyle.publicLbl;
          }
        }

        res = await cstmrPost({
          cuid,
          rlid,
          ...((key !== 'home') && { piid }),
          urlPath: config.town.back.qr.folder.edit,
          payload,
        });
        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        } else if (answerRefusesPrivilege(res)) {
          error = new Error('notAllowed');
        }
      } catch (err) {
        error = err;
      }
      dispatch({
        type: 'setFoldersStatus',
        piid: key,
        value: 'ready',
      });

      const rslt = {};
      if (error) {
        logError(
          `editQrFolder Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
          'QrsProvider.editQrFolder.error',
          {
            err: error?.message ?? String(error), // NOSONAR error stringified for logging
            ...(error?.status && { status: error.status }),
            ...(res?.status && { resStatus: res.status }),
            qrFldrId,
            qrFldrParentId,
          },
        );
        rslt.error = error;
      }

      await refreshQrFolders(piid);

      return rslt;
    },
    [
      authStatus, state.qrFoldersStatusByPiid, cstmrPost, cuid, rlid,
      refreshQrFolders,
    ],
  );

  const deleteQrFolder = useCallback(
    async (qrFldrId, piid) => {
      if (authStatus !== 'loggedIn') return ({ error: 'notLoggedIn' });
      let error;
      let res;
      const key = folderKey(piid);

      if (state.qrFoldersStatusByPiid[key] !== 'ready') {
        logError(
          'deleteQrFolder, not ready',
          'QrsProvider.deleteQrFolder.notReady',
          {
            key,
            status: state.qrFoldersStatusByPiid[key],
            qrFldrId,
          },
        );
        return ({ error: 'notReady' });
      }

      dispatch({
        type: 'setFoldersStatus',
        piid: key,
        value: 'busy',
      });
      try {
        res = await cstmrPost({
          cuid,
          rlid,
          ...((key !== 'home') && { piid }),
          urlPath: config.town.back.qr.folder.delete,
          payload: {
            qrFldrId,
          },
        });
        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        } else if (answerRefusesPrivilege(res)) {
          error = new Error('notAllowed');
        }
      } catch (err) {
        error = err;
      }
      dispatch({
        type: 'setFoldersStatus',
        piid: key,
        value: 'ready',
      });

      const rslt = {};
      if (error) {
        logError(
          `deleteQrFolder Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
          'QrsProvider.deleteQrFolder.error',
          {
            err: error?.message ?? String(error), // NOSONAR error stringified for logging
            ...(error?.status && { status: error.status }),
            ...(res?.status && { resStatus: res.status }),
            qrFldrId,
          },
        );
        rslt.error = error;
      }

      return rslt;
    },
    [authStatus, state.qrFoldersStatusByPiid, cstmrPost, cuid, rlid],
  );

  const editQrTemplate = useCallback(
    async (qrTemplate, piid) => {
      if (authStatus !== 'loggedIn') return false;
      let error;
      let res;

      try {
        const {
          qrTmpltQtid, qrTmpltLbl, ccatSel, typeOptions,
          ccatSelAuto, ccatSelForceOn, ccatSelForceOff,
          qrTmpltFunctions, pollIds, commIds, gating,
        } = qrTemplate;

        const payload = {
          qrTmpltQtid,
          // This field is displayed with a prefix; it is expected to often have a leading space character
          qrTmpltLbl: qrTmpltLbl.replace(/\s+/g, ' ').trimEnd(),
          qrTmpltFunctions,
        };
        if (qrTmpltFunctions.includes('report')) {
          payload.ccatSel = ccatSel;
          payload.typeOptions = typeOptions;
          payload.ccatSelAuto = ccatSelAuto;
          payload.ccatSelForceOn = ccatSelForceOn;
          payload.ccatSelForceOff = ccatSelForceOff;
        }
        if (qrTmpltFunctions.includes('consult')) {
          payload.pollIds = pollIds;
        }
        if (qrTmpltFunctions.includes('inform')) {
          payload.commIds = commIds;
        }
        if (gating) payload.gating = gating;

        // console.log(`Edit QR code template at ${url}, payload=${displayJson(payload)}`);
        // console.log(` payload=${displayJson(payload)}`);
        res = await cstmrPost({
          cuid,
          rlid,
          ...((folderKey(piid) !== 'home') && { piid }),
          urlPath: config.town.back.qr.template.edit,
          payload,
        });

        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (!error) {
        return true;
      }

      logError(
        `editQrTemplate Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'QrsProvider.editQrTemplate.error',
        {
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
        },
      );
      return false;
    },
    [authStatus, cstmrPost, cuid, rlid],
  );

  const deleteQrTemplate = useCallback(
    async (qrTmpltQtid, piid) => {
      if (authStatus !== 'loggedIn') return false;
      let error;
      let res;

      try {
        const payload = { qrTmpltQtid };

        console.log(`Delete QR template payload=${displayJson(payload)}`);
        // console.log(` newStaffArray=${displayJson(newStaffArray)}`);
        res = await cstmrPost({
          cuid,
          rlid,
          ...((folderKey(piid) !== 'home') && { piid }),
          urlPath: config.town.back.qr.template.delete,
          payload,
        });

        if (res.status !== 200) {
          error = new Error(`Response status=${res.status}`);
        }
      } catch (err) {
        error = err;
      }

      if (!error) {
        return true;
      }
      logError(
        `deleteQrTemplate Error:${error?.message ?? error}`, // NOSONAR error stringified for logging
        'QrsProvider.deleteQrTemplate.error',
        {
          err: error?.message ?? String(error), // NOSONAR error stringified for logging
          ...(error?.status && { status: error.status }),
        },
      );
      return false;
    },
    [authStatus, cstmrPost, cuid, rlid],
  );

  const increaseFolderDepth = useCallback(
    () => {
      dispatch({
        type: 'setFoldersDepth',
        value: state.qrFoldersDepth + 2,
      });
    },
    [state.qrFoldersDepth],
  );

  // Re-fetch folders when depth changes (skip initial value)
  const depthInitRef = useRef(true);
  useEffect(
    () => {
      if (depthInitRef.current) {
        depthInitRef.current = false;
        return;
      }
      Object.keys(state.qrFoldersStatusByPiid).forEach((key) => {
        if (state.qrFoldersStatusByPiid[key] === 'init') return;
        // Use void to make it clear we do not want to wait this async function:
        void refreshQrFolders((key === 'home') ? undefined : key);
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.qrFoldersDepth],
  );

  // Subscribe to push notifications
  usePushEvent(
    'qrEdit',
    () => {
      console.log('WS qrEdit, launch refreshQrCodes');
      // Use void to make it clear we do not want to wait this async function:
      void refreshQrCodes();
    },
  );
  usePushEvent(
    'qrFldrEdit',
    () => {
      console.log('WS qrFldrEdit, launch refreshQrFolders');
      // Use void to make it clear we do not want to wait this async function:
      void refreshQrFolders();
    },
  );
  usePushEvent(
    'qrTmpltEdit',
    () => {
      console.log('WS qrTmpltEdit, launch refreshQrTemplates');
      // Use void to make it clear we do not want to wait this async function:
      void refreshQrTemplates();
    },
  );

  const contextValue = useMemo(
    () => ({
      qrsStore: {
        editQrCode,
        deleteQrCode,
        disableQrCode,
        enableQrCode,
        editQrTemplate,
        deleteQrTemplate,
      },
      qrCodes: {
        byPiid: state.qrCodesByPiid,
        status: state.qrCodesStatusByPiid,
        refresh: refreshQrCodes,
      },
      qrFolders: {
        byPiid: state.qrFoldersByPiid,
        status: state.qrFoldersStatusByPiid,
        refresh: refreshQrFolders,
        increaseFolderDepth,
        plans: state.plans,

        // Folder mutations (home only)
        createQrFolder,
        editQrFolder,
        deleteQrFolder,
      },
      qrTemplates: {
        byPiid: state.qrTemplatesByPiid,
        status: state.qrTemplatesStatusByPiid,
        refresh: refreshQrTemplates,
      },
    }),
    [
      createQrFolder, state.qrFoldersByPiid, state.qrFoldersStatusByPiid, state.plans, editQrFolder,
      deleteQrFolder, refreshQrFolders, increaseFolderDepth, disableQrCode, enableQrCode,
      editQrCode, deleteQrCode,
      state.qrCodesByPiid, state.qrCodesStatusByPiid, refreshQrCodes,
      editQrTemplate, deleteQrTemplate,
      state.qrTemplatesByPiid, state.qrTemplatesStatusByPiid, refreshQrTemplates,
    ],
  );

  return (
    <QrsContext.Provider value={contextValue}>
      {children}
    </QrsContext.Provider>
  );
}

QrsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export default QrsProvider;
